import React, { useEffect, useState } from 'react';
import { Button } from './ui/button';
import { saasFinancialReport, saasFinancialPdfUrl, saasFinancialEmail } from '../api';
import { toast } from '../hooks/use-toast';
import { BarChart3, Printer, TrendingUp, TrendingDown, Percent, Wallet, Mail } from 'lucide-react';

const iso = (d) => d.toISOString().slice(0, 10);
const fmt = (n) => Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const presets = (lang) => {
  const now = new Date();
  const y = now.getFullYear(); const m = now.getMonth();
  const q = Math.floor(m / 3) * 3;
  return [
    { id: 'month', label: lang === 'ar' ? 'هذا الشهر' : 'This month', from: iso(new Date(y, m, 1)), to: iso(now) },
    { id: 'last', label: lang === 'ar' ? 'الشهر الماضي' : 'Last month', from: iso(new Date(y, m - 1, 1)), to: iso(new Date(y, m, 0)) },
    { id: 'quarter', label: lang === 'ar' ? 'هذا الربع' : 'This quarter', from: iso(new Date(y, q, 1)), to: iso(now) },
    { id: 'ytd', label: lang === 'ar' ? 'منذ بداية السنة' : 'Year to date', from: iso(new Date(y, 0, 1)), to: iso(now) },
  ];
};

export const FinancialReportCard = ({ lang, company }) => {
  const ps = presets(lang);
  const [from, setFrom] = useState(ps[0].from);
  const [to, setTo] = useState(ps[0].to);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [mailing, setMailing] = useState(false);
  const [mode, setMode] = useState('full');
  const modes = [
    { id: 'full', label: lang === 'ar' ? 'تقرير كامل' : 'Full report', hint: lang === 'ar' ? 'يشمل أفضل العملاء والموردين وتفاصيل كل العمليات' : 'Includes top customers/suppliers and every transaction' },
    { id: 'summary', label: lang === 'ar' ? 'تقرير ملخّص' : 'Summary report', hint: lang === 'ar' ? 'الأرقام الرئيسية + المؤشرات المالية + التفصيل الشهري فقط' : 'Key Figures + Financial Indicators + Monthly Breakdown only' },
  ];

  const emailMe = async () => {
    setMailing(true);
    try {
      const r = await saasFinancialEmail(from, to, mode);
      toast({ title: lang === 'ar' ? `📧 أُرسل التقرير إلى ${r.to}` : `📧 Report sent to ${r.to}` });
    } catch (e) {
      toast({ title: e?.response?.data?.detail || (lang === 'ar' ? 'تعذّر الإرسال' : 'Could not send'), variant: 'destructive' });
    } finally { setMailing(false); }
  };

  useEffect(() => {
    if (!from || !to || from > to) return;
    setLoading(true); setError('');
    saasFinancialReport(from, to, company || undefined)
      .then(setData)
      .catch((e) => setError(e?.response?.data?.detail || 'Failed'))
      .finally(() => setLoading(false));
  }, [from, to, company]);

  const k = data?.kpis;
  const inputCls = 'h-9 px-2 rounded-md bg-white border border-[#e5e7eb] text-xs text-[#111827] focus:outline-none focus:ring-2 focus:ring-[#2563eb]/30';
  const kpi = [
    { id: 'sales', label: lang === 'ar' ? 'إجمالي المبيعات' : 'Total Sales', val: k?.total_sales, sub: `${k?.sales_count || 0} ${lang === 'ar' ? 'عملية' : 'txns'}`, icon: TrendingUp, color: 'text-[#2563eb]' },
    { id: 'purchases', label: lang === 'ar' ? 'إجمالي المشتريات' : 'Total Purchases', val: k?.total_purchases, sub: `${k?.purchase_count || 0} ${lang === 'ar' ? 'عملية' : 'txns'}`, icon: TrendingDown, color: 'text-rose-600' },
    { id: 'gross', label: lang === 'ar' ? 'الربح الإجمالي' : 'Gross Profit', val: k?.gross_profit, sub: `${lang === 'ar' ? 'هامش' : 'Margin'} ${k?.gross_margin_pct ?? 0}%`, icon: Percent, color: (k?.gross_profit ?? 0) >= 0 ? 'text-[#2563eb]' : 'text-rose-600' },
    { id: 'net', label: lang === 'ar' ? 'صافي الربح' : 'Net Profit', val: k?.net_profit, sub: `${lang === 'ar' ? 'هامش صافي' : 'Net margin'} ${k?.net_margin_pct ?? 0}%`, icon: Wallet, color: (k?.net_profit ?? 0) >= 0 ? 'text-[#2563eb]' : 'text-rose-600' },
  ];

  return (
    <div className="glass-card rounded-2xl p-5 mb-6" data-testid="financial-report-card">
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="flex items-center gap-2">
          <BarChart3 className="w-4 h-4 text-[#2563eb]" />
          <h3 className="font-display text-lg text-[#0a0a0a]">{lang === 'ar' ? 'تقرير المبيعات والمشتريات' : 'Sales & Purchases Report'}</h3>
        </div>
        <div className="ms-auto flex flex-wrap items-center gap-2">
          <input type="date" value={from} max={to} onChange={(e) => setFrom(e.target.value)} className={inputCls} data-testid="fin-date-from" />
          <span className="text-xs text-[#111827]/40">→</span>
          <input type="date" value={to} min={from} onChange={(e) => setTo(e.target.value)} className={inputCls} data-testid="fin-date-to" />
          <Button onClick={emailMe} disabled={mailing || !from || !to || from > to} className="ghost-button h-9 px-3 text-xs bg-transparent rounded-md" data-testid="fin-email-btn">
            <Mail className="w-3.5 h-3.5 me-1" /> {mailing ? '...' : (lang === 'ar' ? 'أرسله لبريدي' : 'Email me')}
          </Button>
          <Button onClick={() => window.open(saasFinancialPdfUrl(from, to, company || undefined, mode), '_blank')} disabled={!from || !to || from > to} className="h-9 px-3 text-xs bg-[#0a0a0a] hover:bg-[#111827] text-white rounded-md" data-testid="fin-pdf-btn">
            <Printer className="w-3.5 h-3.5 me-1" /> {lang === 'ar' ? 'تحميل PDF' : 'Download PDF'}
          </Button>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2 mb-3" data-testid="fin-mode-switch">
        <span className="text-[10px] uppercase tracking-widest text-[#111827]/50 font-bold me-1">{lang === 'ar' ? 'نوع التقرير' : 'Report type'}</span>
        <div className="inline-flex rounded-md border border-[#e5e7eb] bg-white p-0.5">
          {modes.map((m) => (
            <button key={m.id} onClick={() => setMode(m.id)} title={m.hint} className={`h-7 px-3 rounded text-[11px] font-semibold transition-colors ${mode === m.id ? 'bg-[#0a0a0a] text-white' : 'text-[#111827]/65 hover:text-[#0a0a0a]'}`} data-testid={`fin-mode-${m.id}`}>{m.label}</button>
          ))}
        </div>
        <span className="text-[10px] text-[#111827]/45" data-testid="fin-mode-hint">{modes.find((m) => m.id === mode)?.hint}</span>
      </div>
      <div className="flex flex-wrap gap-1.5 mb-4">
        {ps.map((p) => (
          <button key={p.id} onClick={() => { setFrom(p.from); setTo(p.to); }} className={`h-7 px-3 rounded-full text-[11px] border transition-colors ${from === p.from && to === p.to ? 'bg-[#2563eb] text-white border-[#2563eb]' : 'bg-white text-[#111827]/70 border-[#e5e7eb] hover:border-[#2563eb]/40'}`} data-testid={`fin-preset-${p.id}`}>{p.label}</button>
        ))}
      </div>
      <div className="text-[10px] text-[#111827]/45 mb-3 flex items-center gap-1" data-testid="fin-monthly-hint"><Mail className="w-3 h-3" /> {lang === 'ar' ? 'يُرسَل تقرير الشهر السابق تلقائياً إلى بريدك (PDF) في اليوم الأول من كل شهر.' : 'Last month\'s report is emailed to you automatically (PDF) on the 1st of every month.'}</div>
      {error && <div className="text-xs text-rose-600 mb-3" data-testid="fin-error">{error}</div>}
      <div className={`grid grid-cols-2 md:grid-cols-4 gap-3 ${loading ? 'opacity-50' : ''}`}>
        {kpi.map((c) => (
          <div key={c.id} className="rounded-xl border border-[#e5e7eb] bg-[#f8fafc] p-3" data-testid={`fin-kpi-${c.id}`}>
            <div className="flex items-center justify-between">
              <span className="text-[10px] uppercase tracking-widest text-[#111827]/50 font-bold">{c.label}</span>
              <c.icon className={`w-3.5 h-3.5 ${c.color}`} />
            </div>
            <div className={`font-display text-xl mt-1 tabular-nums ${c.color}`}>{fmt(c.val)} <span className="text-[10px] text-[#111827]/40">AED</span></div>
            <div className="text-[10px] text-[#111827]/50 mt-0.5">{c.sub}</div>
          </div>
        ))}
      </div>
      {k && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-2 mt-3 text-[11px]" data-testid="fin-indicators">
          {[
            [lang === 'ar' ? 'المشتريات / المبيعات' : 'Purchases / Sales', `${k.purchase_ratio_pct}%`],
            [lang === 'ar' ? 'متوسط البيع' : 'Avg sale', fmt(k.avg_sale)],
            [lang === 'ar' ? 'متوسط الشراء' : 'Avg purchase', fmt(k.avg_purchase)],
            [lang === 'ar' ? 'مبيعات يومية' : 'Daily sales', fmt(k.avg_daily_sales)],
            [lang === 'ar' ? 'الرواتب' : 'Salaries', fmt(k.salaries)],
            [lang === 'ar' ? 'المرتجعات' : 'Refunds', fmt(k.refunds)],
          ].map(([l, v]) => (
            <div key={l} className="flex items-center justify-between rounded-md bg-white border border-[#e5e7eb] px-2.5 py-1.5">
              <span className="text-[#111827]/55">{l}</span><span className="font-semibold text-[#0a0a0a] tabular-nums">{v}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
